import * as React from 'react'
import PageLayout from '@/components/ui/page-layout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useFastboundFiles } from '@/features/subscriptions/api/get-fastbound-files'
import { useFastboundFileContent } from '@/features/subscriptions/api/get-fastbound-file-content'
import { useUploadFastboundFile } from '@/features/subscriptions/api/upload-fastbound-file'
import { useSyncFastboundFile } from '@/features/subscriptions/api/sync-fastbound-file'
import { Loader2, FileText, Download, Upload, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"

export default function FastboundFilesPage() {
    const [selectedFile, setSelectedFile] = React.useState<string | null>(null)
    const [uploadStatus, setUploadStatus] = React.useState<string | null>(null)
    const [syncStatus, setSyncStatus] = React.useState<string | null>(null)
    const { data: files, isLoading: isLoadingFiles, isError: isErrorFiles } = useFastboundFiles()
    const fileInputRef = React.useRef<HTMLInputElement | null>(null)
    const syncInputRef = React.useRef<HTMLInputElement | null>(null)
    const uploadMutation = useUploadFastboundFile({
        mutationConfig: {
            onSuccess: () => setUploadStatus('Upload successful. Parsing file...'),
            onError: () => setUploadStatus('Upload failed. Please try again.'),
        },
    })
    const syncMutation = useSyncFastboundFile({
        mutationConfig: {
            onSuccess: () => setSyncStatus('Sync started. You will see updates once processing completes.'),
            onError: () => setSyncStatus('Sync failed. Please verify the CSV and try again.'),
        },
    })
    
    const { data: fileContent, isLoading: isLoadingContent } = useFastboundFileContent({
        name: selectedFile || '',
    })

    const [csvData, setCsvData] = React.useState<{ headers: string[], rows: string[][] } | null>(null)

    React.useEffect(() => {
        if (!selectedFile && files && files.length > 0) {
            setSelectedFile(files[0].name)
        }
    }, [files, selectedFile])

    React.useEffect(() => {
        if (fileContent) {
            const reader = new FileReader()
            reader.onload = (e) => {
                const text = e.target?.result as string
                if (text) {
                    const lines = text.split('\n').map(l => l.trim()).filter(l => l)
                    if (lines.length > 0) {
                        // Simple CSV parser - handles basic quoted values
                        const parseLine = (line: string) => {
                            const values: string[] = []
                            let current = ''
                            let inQuotes = false
                            
                            for (let i = 0; i < line.length; i++) {
                                const char = line[i]
                                if (char === '"') {
                                    if (inQuotes && line[i + 1] === '"') {
                                        current += '"'
                                        i++
                                    } else {
                                        inQuotes = !inQuotes
                                    }
                                } else if (char === ',' && !inQuotes) {
                                    values.push(current)
                                    current = ''
                                } else {
                                    current += char
                                }
                            }
                            values.push(current)
                            return values
                        }

                        const headers = parseLine(lines[0])
                        const rows = lines.slice(1).map(parseLine)
                        setCsvData({ headers, rows })
                    }
                }
            }
            reader.readAsText(fileContent)
        } else {
            setCsvData(null)
        }
    }, [fileContent])

    const handleDownload = () => {
        if (!selectedFile || !fileContent) return
        const url = window.URL.createObjectURL(fileContent)
        const a = document.createElement('a')
        a.href = url
        a.download = selectedFile
        a.click()
        window.URL.revokeObjectURL(url)
    }

    const handleUploadClick = () => {
        fileInputRef.current?.click()
    }

    const handleUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return
        setUploadStatus('Uploading file...')
        uploadMutation.mutate(file, {
            onSettled: () => {
                event.target.value = ''
            },
        })
    }

    const handleSyncClick = () => {
        syncInputRef.current?.click()
    }

    const handleSyncChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) return
        setSyncStatus('Uploading file for sync...')
        syncMutation.mutate(file, {
            onSettled: () => {
                event.target.value = ''
            },
        })
    }

    return (
        <PageLayout 
            title='Fastbound Files' 
            subtitle='View uploaded Fastbound subscription files' 
            breadcrumbs={[
                { label: 'Subscriptions', href: '/subscriptions' }, 
                { label: 'Fastbound Files', href: '/subscriptions/fastbound-files' }
            ]}
        >
            <div className="flex h-[calc(100vh-12rem)] gap-6">
                {/* Sidebar - File List */}
                <Card className="w-80 flex-shrink-0 flex flex-col">
                    <CardHeader className="border-b py-4 flex flex-row items-center justify-between space-y-0">
                        <CardTitle className="text-lg">Files</CardTitle>
                        <div className="flex items-center gap-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".csv"
                                className="hidden"
                                onChange={handleUploadChange}
                            />
                            <Button size="sm" variant="outline" onClick={handleUploadClick} disabled={uploadMutation.isPending}>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 overflow-y-auto p-0">
                        {uploadStatus && (
                            <div className="px-4 py-2 text-xs text-muted-foreground">
                                {uploadStatus}
                            </div>
                        )}
                        {isLoadingFiles ? (
                            <div className="flex justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        ) : isErrorFiles ? (
                            <div className="p-4 text-destructive text-sm">Failed to load files</div>
                        ) : files && files.length > 0 ? (
                            <div className="divide-y">
                                {files.map((file) => (
                                    <button
                                        key={file.name}
                                        onClick={() => setSelectedFile(file.name)}
                                        className={cn(
                                            "w-full text-left px-4 py-3 hover:bg-muted transition-colors focus:outline-none focus:bg-muted",
                                            selectedFile === file.name && "bg-muted font-medium"
                                        )}
                                    >
                                        <div className="flex items-center gap-3">
                                            <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                            <div className="min-w-0">
                                                <div className="text-sm truncate" title={file.name}>
                                                    {file.name}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    {file.lastModified ? new Date(file.lastModified).toLocaleDateString() : '-'}
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <div className="p-4 text-sm text-muted-foreground text-center">
                                No files found
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Main Content - CSV View */}
                <Card className="flex-1 flex flex-col min-w-0">
                    <CardHeader className="border-b py-4 flex flex-row items-center justify-between space-y-0">
                        <CardTitle className="text-lg truncate pr-4">
                            {selectedFile || 'Select a file'}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                            <input
                                ref={syncInputRef}
                                type="file"
                                accept=".csv"
                                className="hidden"
                                onChange={handleSyncChange}
                            />
                            <Button size="sm" variant="outline" onClick={handleSyncClick} disabled={syncMutation.isPending}>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Sync
                            </Button>
                            {selectedFile && (
                                <Button size="sm" variant="outline" onClick={handleDownload}>
                                    <Download className="h-4 w-4 mr-2" />
                                    Download
                                </Button>
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="flex-1 overflow-auto p-0">
                        {syncStatus && (
                            <div className="px-4 py-2 text-xs text-muted-foreground">
                                {syncStatus}
                            </div>
                        )}
                        {selectedFile ? (
                            isLoadingContent ? (
                                <div className="flex items-center justify-center h-full">
                                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                                </div>
                            ) : csvData ? (
                                <div className="relative">
                                    <Table>
                                        <TableHeader className="sticky top-0 bg-background z-10 shadow-sm">
                                            <TableRow>
                                                {csvData.headers.map((header, i) => (
                                                    <TableHead key={i} className="whitespace-nowrap">
                                                        {header}
                                                    </TableHead>
                                                ))}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {csvData.rows.map((row, i) => (
                                                <TableRow key={i}>
                                                    {row.map((cell, j) => (
                                                        <TableCell key={j} className="whitespace-nowrap">
                                                            {cell}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-full text-muted-foreground">
                                    Failed to load or parse file content
                                </div>
                            )
                        ) : (
                            <div className="flex items-center justify-center h-full text-muted-foreground">
                                Select a file from the list to view content
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </PageLayout>
    )
}

